import React, { useState, useEffect, useMemo } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { UserProfile } from '../types/user';
import { PresentationRole } from '../types/role';
import { AdvancedLifeAnalysisResult, LifeThemeId, LifeThemeQuestion } from '../types/advancedLifeAnalysis';
import { LIFE_THEMES_REGISTRY } from '../utils/advancedLifeAnalysisTaxonomy';
import { runAdvancedLifeAnalysis } from '../utils/advancedLifeAnalysisEngine';

// Role Switcher & Router
import { RoleSwitcher } from './role/RoleSwitcher';
import { RolePresentationRouter } from './role/RolePresentationRouter';

// Modals
import { ClassicalReasoningModal } from './ClassicalReasoningModal';
import { AdvancedPrintReport } from './AdvancedPrintReport';
import { LifeDomainEvidenceMatrixModal } from './LifeDomainEvidenceMatrixModal';
import { Layers } from 'lucide-react';

interface AdvancedLifeAnalysisViewProps {
  kundali: CompleteKundali;
  lang: Language;
  chartStyle: ChartStyle;
  setChartStyle: (style: ChartStyle) => void;
  currentUser?: UserProfile | null;
  onOpenConsultation?: (prompt?: string) => void;
  initialRole?: PresentationRole;
  onRoleChange?: (role: PresentationRole) => void;
}

export const AdvancedLifeAnalysisView: React.FC<AdvancedLifeAnalysisViewProps> = ({
  kundali,
  lang,
  chartStyle,
  setChartStyle,
  currentUser,
  onOpenConsultation,
  initialRole,
  onRoleChange
}) => {
  const isHi = lang === 'hi';
  const allThemes = Object.values(LIFE_THEMES_REGISTRY);
  const [isEvidenceMatrixOpen, setIsEvidenceMatrixOpen] = useState(false);

  // Active Presentation Role State (Persisted in localStorage)
  const [internalRole, setInternalRole] = useState<PresentationRole>(() => {
    if (initialRole) return initialRole;
    try {
      const saved = localStorage.getItem('v515_active_presentation_role') as PresentationRole;
      if (saved && ['BASIC', 'PRO', 'ACHARYA'].includes(saved)) {
        return saved;
      }
    } catch {
      // ignore
    }
    return 'PRO';
  });

  const activeRole = initialRole || internalRole;

  const handleRoleChange = (newRole: PresentationRole) => {
    setInternalRole(newRole);
    try {
      localStorage.setItem('v515_active_presentation_role', newRole);
    } catch {
      // ignore
    }
    if (onRoleChange) {
      onRoleChange(newRole);
    }
  };

  const [selectedThemeId, setSelectedThemeId] = useState<LifeThemeId>('career_job');
  const currentTheme = LIFE_THEMES_REGISTRY[selectedThemeId] || allThemes[0];

  const [selectedQuestionId, setSelectedQuestionId] = useState<string>(
    currentTheme.questions[0]?.id || ''
  );

  // Update selected question when theme changes
  useEffect(() => {
    if (currentTheme.questions && currentTheme.questions.length > 0) {
      setSelectedQuestionId(currentTheme.questions[0].id);
    }
  }, [selectedThemeId]);

  // Modal states
  const [isReasoningModalOpen, setIsReasoningModalOpen] = useState<boolean>(false);
  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);
  const [printModalRole, setPrintModalRole] = useState<PresentationRole>('PRO');

  // AI Acharya Consultation State
  const [aiInterpretation, setAiInterpretation] = useState<string>('');
  const [isAiLoading, setIsAiLoading] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string>('');
  const [customQuestionInput, setCustomQuestionInput] = useState<string>('');
  const [aiQuota, setAiQuota] = useState<{ allowance: number; remaining: number; resetDate: string } | null>(null);

  // Fetch AI Quota
  const fetchQuota = async () => {
    try {
      const res = await fetch(`/api/user/ai-quota?userId=${currentUser?.uid || 'guest'}&role=${currentUser?.role || 'guest'}`);
      if (res.ok) {
        const data = await res.json();
        if (data.quota) setAiQuota(data.quota);
      }
    } catch (e) {
      console.warn('Failed to fetch AI quota:', e);
    }
  };

  useEffect(() => {
    fetchQuota();
  }, [currentUser?.uid, currentUser?.role]);

  // Compute Deterministic Analysis Result (Unchanged Mathematical Engine)
  const analysisResult: AdvancedLifeAnalysisResult = useMemo(() => {
    return runAdvancedLifeAnalysis(kundali, selectedThemeId, selectedQuestionId);
  }, [kundali, selectedThemeId, selectedQuestionId]);

  // Request Server AI Jyotish Acharya Analysis
  const handleRequestAiAnalysis = async () => {
    setIsAiLoading(true);
    setAiError('');
    try {
      const res = await fetch('/api/astrology/advanced-life-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kundali,
          themeId: selectedThemeId,
          questionId: selectedQuestionId,
          lang,
          userId: currentUser?.uid || 'guest',
          userRole: currentUser?.role || 'guest',
          customNotes: customQuestionInput
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate AI consultation');
      }

      setAiInterpretation(data.aiInterpretation || '');
      if (data.quota) setAiQuota(data.quota);
    } catch (err: any) {
      setAiError(err.message || 'An error occurred while generating analysis.');
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleOpenPrintReport = (mode?: 'BASIC' | 'PRO' | 'ACHARYA') => {
    setPrintModalRole(mode || activeRole);
    setShowPrintModal(true);
  };

  return (
    <div
      id="advanced-life-analysis-workbench"
      className="w-full px-3 sm:px-4 md:px-6 py-6 space-y-6 animate-fadeIn text-astro-text"
    >
      {/* Top Bar: Global Presentation Role Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-astro-bg p-3 sm:p-4 rounded-2xl border border-astro-border shadow-xs">
        <div>
          <h2 className="text-sm sm:text-base font-bold text-astro-text font-serif">
            {isHi ? 'वैदिक ज्योतिष जीवन अनुसंधान कार्यस्थल' : 'Vedic Astrology Life Inquiry Workbench'}
          </h2>
          <p className="text-[11px] text-astro-secondary-text">
            {isHi
              ? 'प्रस्तुति दृष्टिकोण का चयन करें — सभी दृश्य समान शास्त्रीय गणितीय आधार पर निर्मित हैं।'
              : 'Select your inquiry perspective — all views are rendered from the same invariant mathematical foundation.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setIsEvidenceMatrixOpen(true)}
            className="px-3 py-1.5 rounded-xl bg-astro-card border border-astro-border hover:bg-white text-astro-primary text-xs font-semibold shadow-2xs transition-all cursor-pointer flex items-center gap-1.5"
          >
            <Layers className="w-4 h-4 text-astro-accent" />
            <span>{isHi ? 'प्रमाण मैट्रिक्स (धारा 46)' : 'Vedic Evidence Matrix (Sec 46)'}</span>
          </button>
          <RoleSwitcher
            activeRole={activeRole}
            onRoleChange={handleRoleChange}
            lang={lang}
            className="w-full sm:w-auto"
          />
        </div>
      </div>

      {/* Main Content: Routed by Active Role */}
      <RolePresentationRouter
        activeRole={activeRole}
        kundali={kundali}
        analysisResult={analysisResult}
        selectedThemeId={selectedThemeId}
        onSelectTheme={setSelectedThemeId}
        selectedQuestionId={selectedQuestionId}
        onSelectQuestion={setSelectedQuestionId}
        lang={lang}
        chartStyle={chartStyle}
        setChartStyle={setChartStyle}
        onOpenReasoningModal={() => setIsReasoningModalOpen(true)}
        onOpenPrintReport={handleOpenPrintReport}
        onSwitchRole={handleRoleChange}
        aiQuota={aiQuota}
        customQuestionInput={customQuestionInput}
        onCustomQuestionChange={setCustomQuestionInput}
        onRequestAiAnalysis={handleRequestAiAnalysis}
        isAiLoading={isAiLoading}
        aiInterpretation={aiInterpretation}
        aiError={aiError}
      />

      {/* Modal: "Why This Conclusion?" Classical Parashari Reasoning */}
      <ClassicalReasoningModal
        isOpen={isReasoningModalOpen}
        onClose={() => setIsReasoningModalOpen(false)}
        analysisResult={analysisResult}
        lang={lang}
      />

      {/* Modal: Advanced Print / PDF Export Report */}
      {showPrintModal && (
        <AdvancedPrintReport
          kundali={kundali}
          analysisResult={analysisResult}
          lang={lang}
          chartStyle={chartStyle}
          initialRole={printModalRole}
          onClose={() => setShowPrintModal(false)}
        />
      )}
    </div>
  );
};
